/**
 * Bill calendar projection helpers
 */

import type { Bill } from '../types/bill.types';
import {
  computeBillStatus,
  getBillOccurrencesInRange,
  advanceDueDate,
  AUTOPAY_GRACE_PERIOD_DAYS,
} from './budget-calculations';

export type BillOccurrenceStatus = 'upcoming' | 'due' | 'overdue' | 'paid';

export interface BillCalendarOccurrence {
  bill: Bill;
  occurrenceDate: string;
  status: BillOccurrenceStatus;
}

function formatLocalDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function paymentWindow(dueDate: string): { from: string; to: string } {
  const [y, m, d] = dueDate.split('-').map(Number);
  const due = new Date(y, (m ?? 1) - 1, d ?? 1);
  const from = new Date(due);
  from.setDate(from.getDate() - 7);
  const to = new Date(due);
  to.setDate(to.getDate() + AUTOPAY_GRACE_PERIOD_DAYS + 7);
  return { from: formatLocalDate(from), to: formatLocalDate(to) };
}

function isPaidForOccurrence(bill: Bill, occurrenceDate: string): boolean {
  if (bill.frequency === 'one_off' && bill.status === 'paid') return true;
  if (!bill.lastPaidAt) return false;
  const paidDay = bill.lastPaidAt.slice(0, 10);
  const { from, to } = paymentWindow(occurrenceDate);
  return paidDay >= from && paidDay <= to;
}

/** Project bills onto occurrence dates within an inclusive range. */
export function buildBillCalendarOccurrences(
  bills: Bill[],
  rangeStart: string,
  rangeEnd: string,
  today: Date = new Date()
): BillCalendarOccurrence[] {
  const results: BillCalendarOccurrence[] = [];

  for (const bill of bills) {
    const anchor = bill.anchorDate || bill.dueDate;
    const dates = getBillOccurrencesInRange(
      anchor,
      bill.frequency,
      bill.dayOfMonth,
      rangeStart,
      rangeEnd
    );

    for (const occurrenceDate of dates) {
      if (isPaidForOccurrence(bill, occurrenceDate)) {
        results.push({ bill, occurrenceDate, status: 'paid' });
        continue;
      }
      results.push({
        bill,
        occurrenceDate,
        status: computeBillStatus(occurrenceDate, false, today),
      });
    }
  }

  return results.sort((a, b) => a.occurrenceDate.localeCompare(b.occurrenceDate));
}

/** Group occurrences by ISO date string. */
export function groupOccurrencesByDate(
  occurrences: BillCalendarOccurrence[]
): Map<string, BillCalendarOccurrence[]> {
  const map = new Map<string, BillCalendarOccurrence[]>();
  for (const item of occurrences) {
    const list = map.get(item.occurrenceDate) ?? [];
    list.push(item);
    map.set(item.occurrenceDate, list);
  }
  return map;
}

export function statusDotClass(status: BillOccurrenceStatus): string {
  switch (status) {
    case 'overdue':
      return 'bg-red-500';
    case 'due':
      return 'bg-yellow-500';
    case 'paid':
      return 'bg-green-500';
    default:
      return 'bg-blue-500';
  }
}

/** Advance a paid occurrence — used only for documentation/tests of cycle math. */
export function nextAfterOccurrence(
  occurrenceDate: string,
  frequency: string,
  dayOfMonth?: number | null
): string {
  return advanceDueDate(occurrenceDate, frequency, dayOfMonth);
}
